import { useState, useEffect } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { ExtensionBridge } from '../lib/extension'

// Helper function to ensure user profile exists
async function ensureProfile(user: User) {
  try {
    console.log('Ensuring profile exists for user:', user.email)
    
    // Get the auth token for API calls
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) {
      console.error('No access token available')
      return
    }

    // Call our backend to ensure profile exists
    const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001'
    const response = await fetch(`${API_BASE}/profile/ensure`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      throw new Error(`Profile creation failed: ${response.status}`)
    }

    const result = await response.json()
    console.log('Profile ensured:', result.profile.email)
  } catch (error) {
    console.error('Error ensuring profile:', error)
    
    // Fallback: try direct Supabase insert
    try {
      const { error: insertError } = await supabase
        .from('profiles')
        .insert({
          id: user.id,
          email: user.email!,
          name: user.user_metadata?.name || user.user_metadata?.full_name || user.email!.split('@')[0]
        })
      
      if (insertError && !insertError.message.includes('duplicate')) {
        console.error('Fallback profile creation failed:', insertError)
      } else {
        console.log('Profile created via fallback')
      }
    } catch (fallbackError) {
      console.error('Fallback profile creation error:', fallbackError)
    }
  }
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Handle OAuth redirect errors
    const urlParams = new URLSearchParams(window.location.search)
    const error = urlParams.get('error')
    const errorDescription = urlParams.get('error_description')
    
    if (error) {
      console.error('OAuth error:', error, errorDescription)
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname)
    }

    // Get initial session with shorter timeout
    console.log('Getting initial session...')
    
    const sessionPromise = supabase.auth.getSession()
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Session fetch timeout after 5s')), 5000)
    )
    
    Promise.race([sessionPromise, timeoutPromise])
      .then((result: any) => {
        console.log('Session promise resolved:', result)
        const { data: { session } = {}, error } = result || {}
        console.log('Initial session result:', { session: !!session, user: session?.user?.email, error })
        if (error) {
          console.error('Session error:', error)
        }
        
        // If user is already signed in on page load, send token to extension
        if (session?.access_token) {
          console.log('Initial session: User already signed in, sending token to extension...', {
            tokenLength: session.access_token.length,
            chromeRuntime: !!window.chrome?.runtime,
            extensionId: (window as any).RESISTOR_EXTENSION_ID
          })
          ExtensionBridge.sendAuthToken(session.access_token).catch(err =>
            console.warn('Failed to send initial auth token to extension:', err)
          )
        }
        
        setSession(session || null)
        setUser(session?.user ?? null)
        setLoading(false)
      })
      .catch((error) => {
        console.error('Session fetch failed or timed out:', error)
        // Set loading to false even on timeout so user can proceed
        setLoading(false)
        setSession(null)
        setUser(null)
      })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', { event, user: session?.user?.email, sessionExists: !!session })
      
      // If user just signed in, ensure profile exists and send token to extension
      if (event === 'SIGNED_IN' && session?.user) {
        console.log('User signed in, ensuring profile...')
        // Don't await this to prevent blocking the auth flow
        ensureProfile(session.user).catch(err => 
          console.error('Profile creation failed:', err)
        )
        
        // Send auth token to extension
        if (session.access_token) {
          console.log('Auth: Attempting to send token to extension...', { 
            tokenLength: session.access_token.length,
            chromeRuntime: !!window.chrome?.runtime,
            extensionId: (window as any).RESISTOR_EXTENSION_ID
          })
          ExtensionBridge.sendAuthToken(session.access_token).catch(err =>
            console.warn('Failed to send auth token to extension:', err)
          )
        }
      }
      
      // If user signed out, clear extension auth
      if (event === 'SIGNED_OUT') {
        ExtensionBridge.clearAuthToken().catch(err =>
          console.warn('Failed to clear extension auth token:', err)
        )
      }
      
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [])

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin
      }
    })
    
    if (error) {
      throw error
    }
  }

  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    if (error) {
      throw error
    }
  }

  return {
    user,
    session,
    loading,
    signInWithGoogle,
    signOut
  }
}